import React, { useEffect, useRef, useState } from 'react';
import { AlertBanner, Button } from '@ygtechnologies/rubus-ui';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { extractErrorMessage } from '@/utils/ajax';
import { EmailPreviewKind, EmailPreviewResult, previewEmail } from '@/services/emailTemplates';
import theme from './EmailTemplates.module.scss';

/** Long enough that a burst of typing renders once, short enough to feel automatic. */
const DEBOUNCE_MS = 1200;

interface TemplatePreviewProps {
    /**
     * Identity of the scope being previewed (the editor target's key). When this changes the template
     * change came from switching context, not from typing, so the preview re-renders immediately.
     */
    contextKey: string;
    kind: EmailPreviewKind;
    template: string;
    subject?: string;
    languageCode: string | null;
    /**
     * Project from the context bar. Content previews resolve the layout that project would really get, so
     * the preview matches what it would actually send.
     */
    projectId: number | null;
    /** Seller profile from the context bar, so profile-scoped previews resolve that profile's layout. */
    profileId: number | null;
    /** Collapses the preview; reopening lives in the editor header, which renders it only while hidden. */
    onHide(): void;
}

/**
 * Renders the template server-side and shows the result, re-rendering automatically a moment after
 * editing stops. The HTML goes into a fully sandboxed iframe: template markup is arbitrary and must not
 * run scripts or reach this origin.
 */
const TemplatePreview: React.FC<TemplatePreviewProps> = ({
    contextKey,
    kind,
    template,
    subject,
    languageCode,
    projectId,
    profileId,
    onHide,
}) => {
    const [result, setResult] = useState<EmailPreviewResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    // What the last render was for, so typing can be told apart from scope/context switches.
    const lastContext = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        // Only debounce edits to the text itself (same context, so MJML is not run per keystroke).
        // The first render and anything that changes the context — scope, project, profile, language —
        // renders straight away.
        const context = [contextKey, kind, languageCode, projectId, profileId].join('|');
        const delay = lastContext.current === context ? DEBOUNCE_MS : 0;
        lastContext.current = context;

        const timer = setTimeout(async () => {
            setIsLoading(true);
            try {
                const next = await previewEmail({ kind, template, subject, languageCode, projectId, profileId });
                if (!cancelled) {
                    setResult(next);
                    setError(null);
                }
            } catch (e) {
                const message = (await extractErrorMessage(e)) ?? 'The template could not be rendered.';
                if (!cancelled) {
                    // Keep the last good render on screen — while typing, a template is briefly
                    // invalid (a half-written {{#if}}) and blanking the preview each time is worse
                    // than showing a slightly stale one alongside the error.
                    setError(message);
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }, delay);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [contextKey, kind, template, subject, languageCode, projectId, profileId]);

    return (
        <div className={theme.preview}>
            <div className={theme.previewHeader}>
                {kind === 'content' && result && (
                    <div className={theme.previewSubject}>
                        <span className={theme.previewSubjectLabel}>Subject</span>
                        <span>{result.subject || <em>(empty)</em>}</span>
                    </div>
                )}
                <div className={theme.previewHeaderControls}>
                    {isLoading && <span className={theme.previewStatus}>Updating…</span>}
                    {projectId === null && (
                        <span className={theme.previewStatus}>
                            Generic sample data — pick a project above to preview its branding and layout.
                        </span>
                    )}
                    <Button size="xs" variant="tertiary" icon={faEyeSlash} onClick={onHide} title="Hide preview" />
                </div>
            </div>

            {error && (
                <AlertBanner type="danger" title="This version could not be rendered">
                    {error}
                    {result && ' The preview below is the last version that rendered.'}
                </AlertBanner>
            )}

            {!!result?.warnings?.length && (
                <AlertBanner
                    type="warning"
                    title={`MJML reported ${result.warnings.length === 1 ? 'an issue' : 'issues'}`}>
                    <ul className={theme.warningList}>
                        {result.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                        ))}
                    </ul>
                </AlertBanner>
            )}

            {result && <iframe title="Email preview" sandbox="" srcDoc={result.html} className={theme.previewFrame} />}

            {!result && !error && (
                <p className={theme.hint}>
                    {kind === 'content'
                        ? 'Renders this content inside the layout it would be sent with, using sample data.'
                        : 'Renders this layout with a placeholder body, using sample data.'}
                </p>
            )}
        </div>
    );
};

export default TemplatePreview;
